import React from "react";

// Renderiza o texto de uma mensagem do mastra como o cliente viu no WhatsApp.
//
// A Gi responde com blocos de template no meio do texto (<CARD>{json}</CARD>, <FOTOS>,
// <CAROUSEL>, <CARD_URL>, <LIST>) que o mastra converte em mensagens do WhatsApp na hora do
// envio (guerini-agent-mastra: services/twilio-template-service.ts). O histórico guarda o texto
// cru, então aqui os blocos viram cards/fotos em vez de JSON.

const BLOCK_REGEX = /<(LIST|FOTOS|CAROUSEL|CARD_URL|CARD)>([\s\S]*?)<\/\1>/g;
const URL_REGEX = /(https?:\/\/[^\s<>"')\]]+)/g;

// O modelo às vezes quebra linha de verdade dentro das strings do JSON, o que é JSON inválido.
// Escapa só as quebras que estão dentro de strings.
const escapeNewlinesInStrings = (raw) => {
  let out = "";
  let inString = false;
  let escaped = false;

  for (const char of raw) {
    if (inString && !escaped && char === "\n") {
      out += "\\n";
      continue;
    }
    if (inString && !escaped && char === "\r") continue;

    if (char === '"' && !escaped) inString = !inString;
    escaped = char === "\\" && !escaped;
    out += char;
  }

  return out;
};

const parseJSON = (raw) => {
  try {
    return JSON.parse(raw);
  } catch (e) {
    try {
      return JSON.parse(escapeNewlinesInStrings(raw));
    } catch (e2) {
      return undefined;
    }
  }
};

// Quebra o texto em segmentos: { type: "text", text } ou { type: <BLOCO>, data }.
export const parseWhatsAppMessage = (text) => {
  const segments = [];
  let last = 0;
  let match;

  BLOCK_REGEX.lastIndex = 0;
  while ((match = BLOCK_REGEX.exec(text)) !== null) {
    if (match.index > last) segments.push({ type: "text", text: text.slice(last, match.index) });

    const data = parseJSON(match[2].trim());
    segments.push(data === undefined ? { type: "text", text: match[0] } : { type: match[1], data });

    last = match.index + match[0].length;
  }

  if (last < text.length) segments.push({ type: "text", text: text.slice(last) });

  return segments.filter((s) => s.type !== "text" || s.text.trim());
};

const linkStyle = {
  color: "#0b57d0",
  fontWeight: 600,
  textDecoration: "underline",
  wordBreak: "break-all",
};

// URLs viram links destacados; *negrito* do WhatsApp vira <strong>.
const RichText = ({ text }) => {
  if (!text) return null;

  const parts = String(text).replace(/\\n/g, "\n").trim().split(URL_REGEX);

  return (
    <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <a key={i} href={part} target="_blank" rel="noopener noreferrer" style={linkStyle}>
            <span role="img" aria-label="link">🔗</span> {part}
          </a>
        ) : (
          part.split(/(\*[^*\n]+\*)/g).map((piece, j) =>
            /^\*[^*\n]+\*$/.test(piece) ? (
              <strong key={`${i}-${j}`}>{piece.slice(1, -1)}</strong>
            ) : (
              <React.Fragment key={`${i}-${j}`}>{piece}</React.Fragment>
            )
          )
        )
      )}
    </div>
  );
};

const imageStyle = {
  display: "block",
  width: "100%",
  maxHeight: 260,
  objectFit: "cover",
  borderRadius: 6,
  marginBottom: 6,
};

const buttonStyle = {
  display: "block",
  marginTop: 8,
  padding: "6px 10px",
  borderTop: "1px solid rgba(0, 0, 0, 0.1)",
  textAlign: "center",
  color: "#0b57d0",
  fontWeight: 600,
};

const blockStyle = {
  background: "rgba(255, 255, 255, 0.7)",
  border: "1px solid rgba(0, 0, 0, 0.08)",
  borderRadius: 8,
  padding: 8,
  margin: "6px 0",
};

const Photo = ({ url }) => (
  <a href={url} target="_blank" rel="noopener noreferrer">
    <img src={url} alt="" style={imageStyle} />
  </a>
);

const Button = ({ label, url }) =>
  url ? (
    <a href={url} target="_blank" rel="noopener noreferrer" style={buttonStyle}>
      {label || url}
    </a>
  ) : (
    <div style={buttonStyle}>{label}</div>
  );

const Block = ({ segment }) => {
  const data = segment.data || {};

  switch (segment.type) {
    case "CARD":
      return (
        <div style={blockStyle}>
          {data.media_path && <Photo url={data.media_path} />}
          <RichText text={data.body} />
          {data.button_text && <Button label={data.button_text} />}
        </div>
      );

    case "FOTOS":
      return (
        <div style={blockStyle}>
          {(data.fotos || []).map((url, i) => (
            <Photo key={i} url={url} />
          ))}
          <RichText text={data.legenda} />
        </div>
      );

    case "CAROUSEL": {
      const cards = Array.isArray(data) ? data : data.cards || [];
      return (
        <div style={{ display: "flex", gap: 8, overflowX: "auto", margin: "6px 0" }}>
          {cards.map((card, i) => (
            <div key={i} style={{ ...blockStyle, minWidth: 200, margin: 0 }}>
              {card.media_path && <Photo url={card.media_path} />}
              <RichText text={card.body} />
              {card.button_url && <Button label={card.button_text || "Ver empreendimento"} url={card.button_url} />}
            </div>
          ))}
        </div>
      );
    }

    case "CARD_URL":
      return (
        <div style={blockStyle}>
          {data.header_text && <strong>{data.header_text}</strong>}
          <RichText text={data.body} />
          <Button label={data.header_text || "Abrir link"} />
        </div>
      );

    case "LIST":
      return (Array.isArray(data) ? data : []).map((item, i) => (
        <WhatsAppMessage key={i} text={typeof item === "string" ? item : JSON.stringify(item)} />
      ));

    default:
      return null;
  }
};

const WhatsAppMessage = ({ text }) => (
  <>
    {parseWhatsAppMessage(String(text || "")).map((segment, i) =>
      segment.type === "text" ? <RichText key={i} text={segment.text} /> : <Block key={i} segment={segment} />
    )}
  </>
);

export default WhatsAppMessage;
